// @flow

import styled, { css } from "styled-components";
import { transparentize } from "polished";
import Icon from "@fdr/static-ui/Icons/";
import { fontNormal, fontCondensed } from "@fdr/static-ui/Typography";
import buildColor from "@fdr/static-ui/ColorPalette";

import {
  generateTransition,
  easeInOut,
  cubic,
  delayTime,
  grow
} from "@fdr/static-ui/Transitions";
import { buildText } from "../../_atoms/Text";
import ButtonDefault from "../../_atoms/ButtonDefault";

export const BetTicketContainer = styled.div`
  width: 100%;
`;

export const BetTicketSection = styled.section`
  width: 100%;
  flex-direction: column;
  z-index: 30;
  bottom: -85px;
  min-height: 100px;
  min-height: calc(100px + constant(safe-area-inset-bottom));
  min-height: calc(100px + env(safe-area-inset-bottom));
  display: flex;
  flex: 0 0 auto;
  background-color: ${buildColor("white", "100")};
  box-shadow: 0 -1px 4px 0 ${transparentize(0.85, buildColor("black", "100"))};
  will-change: bottom, opacity;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  transition: ${generateTransition(cubic, "bottom")},
    ${generateTransition(easeInOut, "opacity")};
  ${(props) =>
    props.isMobile
      ? css`
          position: fixed;
        `
      : css`
          position: sticky;
        `};
  ${(props) =>
    props.isVisible
      ? css`
          bottom: 0;
          opacity: 1;
          transition-delay: ${delayTime};
        `
      : css`
          opacity: 0;
        `};

  &::before {
    ${(props) =>
      props.isVisible &&
      css`
        content: "";
      `};
    background: transparent;
    height: ${({ hasOnboardingTooltip }) =>
      hasOnboardingTooltip ? "35px" : "20px"};
    position: absolute;
    top: ${({ hasOnboardingTooltip }) =>
      hasOnboardingTooltip ? "-35px" : "-20px"};
    width: 100%;
  }
`;

export const TalentPicksWarning = styled.div`
  position: sticky;
  bottom: 85px;
`;

export const BetTypeTrashContainer = styled.div`
  display: flex;
  width: 56px;
  flex: 0 0 auto;
  flex-direction: column;
  background-color: ${buildColor("white", "100")};
  align-items: center;
  justify-content: space-between;
  border-top-left-radius: 10px;
  overflow: hidden;
`;

export const WagersSubmitContainer = styled.div`
  flex-grow: 1;
  min-width: 0;
  padding-bottom: 0;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const ControlsContainer = styled.div`
  display: flex;
  padding: 0 16px 12px 0;
`;

export const WagersContainer = styled.div`
  width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: auto;
  display: flex;
  align-items: center;
  padding-top: 6px;
  ${({ hasInlineMessage }) =>
    hasInlineMessage &&
    css`
      padding-top: 4px;
      padding-bottom: 8px;
    `};
`;

export const BetTypeText = styled(
  buildText({
    tag: "span",
    align: "center",
    fontSize: 14,
    fontFamily: fontNormal
  })
)`
  display: flex;
  position: relative;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  height: 28px;
  width: 100%;
  background-color: ${buildColor("white", "100")};

  &::after {
    content: "";
    right: 0;
    position: absolute;
    height: 70%;
    width: 1px;
    background-color: ${buildColor("grey", "300")};
  }
`;

export const SelectionsWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  flex-wrap: nowrap;
  overflow: auto;
  height: 32px;
  padding: 0 ${({ hasRaceTitle }) => (hasRaceTitle ? "8px" : "16px")};
  ${({ hasScroll }) =>
    hasScroll &&
    css`
      /* solves overflow scroll right padding problem */
      &::after {
        content: "";
        display: block;
        height: 100%;
        min-width: ${({ hasRaceTitle }) => (hasRaceTitle ? "8px" : "16px")};
      }
    `};

  & > div > ol {
    flex-basis: 0;

    & li > span {
      animation: ${grow} 0.25s;
    }

    &:not(:first-child) {
      margin-left: ${({ hasRaceTitle }) => (hasRaceTitle ? "16px" : "8px")};
    }

    ${({ hasRaceTitle }) =>
      !hasRaceTitle &&
      css`
        &:not(:last-child):after {
          content: "\\00a0";
          margin-left: 8px;
          height: 70%;
          width: 1px;
          background-color: ${buildColor("grey", "300")};
        }
      `}
  }
`;

export const SelectionsContainer = styled.div`
  display: inline-flex;
`;

export const SelectionsEmpty = styled(
  buildText({ tag: "strong", fontSize: 14 })
)`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  height: 28px;
`;

export const HelpButtonWrapper = styled.div`
  margin-right: 16px;
`;

export const InnerHelpButtonWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const IconExclamationContainer = styled.span`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${buildColor("white", "100")};
  border-radius: 12px;
  width: 16px;
  height: 16px;
`;

export const Cancel = styled(ButtonDefault)`
  width: 100%;
  border: 0;
  border-radius: 0;
  background-color: transparent;
`;

export const Add = styled(Icon)`
  border-radius: 50%;
  border: 2px solid ${buildColor("green", "500")};
  margin-left: 4px;
`;

export const PotReturnWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

export const PotReturnSpan = styled.span`
  font-family: ${fontCondensed};
  font-size: 12px;
  text-align: center;
  text-transform: uppercase;
`;

export const FloatingBannerWrapper = styled.div`
  position: absolute;
  bottom: 100px;
  bottom: calc(100px + constant(safe-area-inset-bottom));
  bottom: calc(100px + env(safe-area-inset-bottom));
  left: 0;
  right: 0;
  padding: 12px;
`;

export const InlineMessageWrapper = styled.div`
  padding: 12px 16px 6px;
`;

export const HelpButton = styled(ButtonDefault)`
  font-size: 12px;
  color: ${buildColor("blue", "D1")};
  font-family: ${fontNormal};
`;

export const HelpButtonOnboardingTooltipAnchor = styled.div`
  position: absolute;
  width: 0;
  height: 0;
`;

export const HelpButtonOnboardingTooltip = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: ${fontCondensed};
  letter-spacing: 1.03px;
  font-size: 12px;
  text-transform: uppercase;
  background-color: ${buildColor("navy", "500")};
  z-index: 11;
  padding-left: 8px;
  position: absolute;
  color: ${buildColor("white", "100")};
  align-items: center;
  right: -40px;
  bottom: 5px;
  border-radius: 2px;

  &::before {
    position: absolute;
    right: 20px;
    bottom: -4px;
    content: "";
    width: 0;
    height: 0;
    border-width: 4px 4px 0;
    border-color: ${buildColor("navy", "500")} transparent transparent
      transparent;
    border-style: solid;
  }
`;

export const HelpButtonOnboardingTooltipButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px;
  background: transparent;
`;
